import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete, Box, Button, Checkbox, Collapse, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import TaskTrackingService from '../../services/TaskTrackingService';
import {
    categoryLabel,
    durationError,
    formatDuration,
    platformChoices,
    platformLabel,
    sourceUrlError,
    useTaskCategories,
    useTaskPlatforms,
} from './taskFields';
import { LANGUAGE_NAMES } from './taskUi';
import useTaskSummary from './useTaskSummary';

// A content type can have more than one task type now, and new tasks get the seeded one (D124)
const DEFAULT_TYPE = {
    VIDEO: 'VIDEO_TRANSLATION',
    TEXT: 'TEXT_TRANSLATION',
    BOOK: 'BOOK_TRANSLATION',
    POST: 'SOCIAL_POST',
    AUDIO: 'DUBBING_AUDIO',
};

export const emptyTaskFields = () => ({
    track: false,
    languages: [],
    projectManager: '',
    watchers: [],
    targetDate: '',
    stageOverrides: {},
    // Per video, the same for every language
    category: '',
    duration: '',
    sourceUrl: '',
    platform: '',
});

const userLabel = (u) => (u.name && u.name !== u.email ? `${u.name} (${u.email})` : u.email);

/**
 * "Track this work" fields for the create dialogs and bulk Start tracking. Shown only to
 * Project Managers and Admins; renders nothing for everyone else. With languageOptions the
 * picker offers only those (the languages the files already have), with a way to show all.
 * singleFile: one file is being tracked, so its duration and source video link can be given.
 * existing: what that one file already has from another language ({ title, categoryKey,
 * durationSeconds, sourceUrl, platform }). Those show read only; starting a language never changes them.
 * bulk: several files are being started, so a category only fills in files that have none.
 */
const TaskFieldsSection = ({
    value,
    onChange,
    contentType,
    defaultLanguages = [],
    languageOptions,
    alwaysOn = false,
    singleFile = false,
    existing = null,
    bulk = false,
}) => {
    const summary = useTaskSummary();
    const categories = useTaskCategories();
    const platforms = useTaskPlatforms();
    const [users, setUsers] = useState([]);
    const [types, setTypes] = useState([]);
    const [customize, setCustomize] = useState(false);
    const [allLanguages, setAllLanguages] = useState(false);
    const canManage = !!summary?.canManage;
    const active = alwaysOn || value.track;

    useEffect(() => {
        if (!canManage || !active || users.length > 0) return;
        TaskTrackingService.assignableUsers().then(setUsers).catch(() => setUsers([]));
        TaskTrackingService.taskTypes().then(setTypes).catch(() => setTypes([]));
    }, [canManage, active, users.length]);

    // PM defaults to me once users are known
    useEffect(() => {
        if (active && !value.projectManager && users.length > 0) {
            const me = (JSON.parse(localStorage.getItem('authUser') || '{}').email || '').toLowerCase();
            if (users.some((u) => u.email === me && u.projectManager)) {
                onChange({ ...value, projectManager: me });
            }
        }
    }, [active, users]); // eslint-disable-line react-hooks/exhaustive-deps

    const stages = useMemo(() => {
        const of = types.filter((t) => t.contentType === contentType);
        const type = of.find((t) => t.key === DEFAULT_TYPE[contentType]) || of[0];
        return (type?.stages || []).filter((s) => !s.retired).sort((a, b) => a.order - b.order);
    }, [types, contentType]);

    if (!canManage) return null;

    const set = (patch) => onChange({ ...value, ...patch });
    const setOverride = (stageKey, patch) =>
        set({ stageOverrides: { ...value.stageOverrides, [stageKey]: { ...(value.stageOverrides[stageKey] || {}), ...patch } } });
    const pms = users.filter((u) => u.projectManager);
    const limited = !allLanguages && languageOptions?.length > 0;
    const badDuration = durationError(value.duration);
    const badLink = sourceUrlError(value.sourceUrl);
    const alreadySet = existing ? `Already set for ${existing.title}. Change it in Edit details.` : '';

    return (
        <Box sx={{ mt: 2, p: alwaysOn ? 0 : 1.5, border: alwaysOn ? 'none' : '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {!alwaysOn && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value.track}
                            onChange={(e) =>
                                set({
                                    track: e.target.checked,
                                    languages: e.target.checked && value.languages.length === 0 ? defaultLanguages : value.languages,
                                })
                            }
                        />
                    }
                    label={<Typography sx={{ fontWeight: 500 }}>Track this work</Typography>}
                />
            )}
            <Collapse in={active}>
                <Autocomplete
                    multiple
                    options={limited ? languageOptions : LANGUAGE_NAMES}
                    value={value.languages}
                    onChange={(_, list) => set({ languages: list })}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            margin="dense"
                            label="Languages to track"
                            helperText={
                                limited
                                    ? 'Languages these files already have.'
                                    : 'Languages not created yet can be tracked too; they start once the language exists.'
                            }
                        />
                    )}
                />
                {languageOptions?.length > 0 && (
                    <Button size="small" onClick={() => setAllLanguages((v) => !v)} sx={{ textTransform: 'none', px: 0.5 }}>
                        {allLanguages ? 'Show only languages these files have' : 'Show all languages'}
                    </Button>
                )}
                <Autocomplete
                    options={pms}
                    value={pms.find((u) => u.email === value.projectManager) || null}
                    getOptionLabel={userLabel}
                    isOptionEqualToValue={(a, b) => a.email === b.email}
                    onChange={(_, u) => set({ projectManager: u ? u.email : '' })}
                    renderInput={(params) => <TextField {...params} margin="dense" label="Project Manager" />}
                />
                <Autocomplete
                    multiple
                    options={users}
                    value={users.filter((u) => value.watchers.includes(u.email))}
                    getOptionLabel={userLabel}
                    isOptionEqualToValue={(a, b) => a.email === b.email}
                    onChange={(_, list) => set({ watchers: list.map((u) => u.email) })}
                    renderInput={(params) => <TextField {...params} margin="dense" label="Watchers" />}
                />
                <TextField
                    type="date"
                    margin="dense"
                    fullWidth
                    label="Posting date"
                    InputLabelProps={{ shrink: true }}
                    value={value.targetDate}
                    onChange={(e) => set({ targetDate: e.target.value })}
                    helperText="When this goes out. Stage due dates are worked back from it. Leave empty to count forward from today."
                />
                {existing?.categoryKey ? (
                    <TextField
                        disabled
                        margin="dense"
                        fullWidth
                        label="Category"
                        value={categoryLabel(categories, existing.categoryKey)}
                        helperText={alreadySet}
                    />
                ) : (
                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Category"
                        value={value.category || ''}
                        onChange={(e) => set({ category: e.target.value })}
                        helperText={bulk ? "Set only on files that don't have a category yet. Change one in Edit details." : undefined}
                    >
                        <MenuItem value="">None</MenuItem>
                        {categories
                            .filter((c) => !c.hidden || c.key === value.category)
                            .map((c) => (
                                <MenuItem key={c.key} value={c.key}>
                                    {c.label}
                                </MenuItem>
                            ))}
                    </TextField>
                )}
                {contentType === 'VIDEO' && existing?.platform && (
                    <TextField
                        disabled
                        margin="dense"
                        fullWidth
                        label="Platform"
                        value={platformLabel(platforms, existing.platform)}
                        helperText={alreadySet}
                    />
                )}
                {contentType === 'VIDEO' && !existing?.platform && (
                    <TextField
                        select
                        margin="dense"
                        fullWidth
                        label="Platform"
                        value={value.platform || ''}
                        onChange={(e) => set({ platform: e.target.value })}
                        helperText={
                            bulk
                                ? "Set only on files that don't have a Platform yet. With one, it shows on the Posting calendar."
                                : 'Where it goes out. With one, it shows on the Posting calendar.'
                        }
                    >
                        <MenuItem value="">None</MenuItem>
                        {platformChoices(platforms, value.platform).map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                                {p.label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {contentType === 'VIDEO' && singleFile && existing?.durationSeconds > 0 && (
                    <TextField
                        disabled
                        margin="dense"
                        fullWidth
                        label="Duration"
                        value={formatDuration(existing.durationSeconds)}
                        helperText={alreadySet}
                    />
                )}
                {contentType === 'VIDEO' && singleFile && !(existing?.durationSeconds > 0) && (
                    <TextField
                        margin="dense"
                        fullWidth
                        label="Duration"
                        placeholder="12:30"
                        value={value.duration || ''}
                        onChange={(e) => set({ duration: e.target.value })}
                        error={!!badDuration}
                        helperText={badDuration || 'minutes:seconds or hours:minutes:seconds'}
                    />
                )}
                {contentType === 'VIDEO' && singleFile && existing?.sourceUrl && (
                    <TextField
                        disabled
                        margin="dense"
                        fullWidth
                        label="Source video link"
                        value={existing.sourceUrl}
                        helperText={alreadySet}
                    />
                )}
                {contentType === 'VIDEO' && singleFile && !existing?.sourceUrl && (
                    <TextField
                        margin="dense"
                        fullWidth
                        label="Source video link"
                        placeholder="https://"
                        value={value.sourceUrl || ''}
                        onChange={(e) => set({ sourceUrl: e.target.value })}
                        error={!!badLink}
                        helperText={badLink || 'Where the original video is, for example a Google Drive link.'}
                    />
                )}
                {stages.length > 0 && (
                    <>
                        <Button
                            size="small"
                            onClick={() => setCustomize((c) => !c)}
                            endIcon={customize ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ mt: 1 }}
                        >
                            Customize stage dates and assignees
                        </Button>
                        <Collapse in={customize}>
                            {stages.map((s) => {
                                const o = value.stageOverrides[s.stageKey] || {};
                                return (
                                    <Box key={s.stageKey} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                                        <Typography variant="body2" sx={{ width: 150 }}>
                                            {s.displayName}
                                            {s.completesOnProcessStage && (
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    {' '}
                                                    (from editor)
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Autocomplete
                                            size="small"
                                            options={users}
                                            value={users.find((u) => u.email === o.plannedAssignee) || null}
                                            getOptionLabel={userLabel}
                                            isOptionEqualToValue={(a, b) => a.email === b.email}
                                            onChange={(_, u) => setOverride(s.stageKey, { plannedAssignee: u ? u.email : null })}
                                            renderInput={(params) => <TextField {...params} label="Assignee" />}
                                            sx={{ flex: '1 1 180px' }}
                                        />
                                        <TextField
                                            size="small"
                                            type="date"
                                            label="Due"
                                            InputLabelProps={{ shrink: true }}
                                            value={o.dueDate || ''}
                                            onChange={(e) => setOverride(s.stageKey, { dueDate: e.target.value || null })}
                                            sx={{ width: 160 }}
                                        />
                                    </Box>
                                );
                            })}
                        </Collapse>
                    </>
                )}
            </Collapse>
        </Box>
    );
};

export default TaskFieldsSection;
